import type { Body, OrbitalElements } from './types';

/**
 * Pure analytic two-body Kepler solver (AD-1). Pure TypeScript: no DOM, canvas,
 * framework, network, storage, or wall-clock access, and no AU, day, or degree
 * literal (SI and radians only, AD-5). Deterministic given its inputs.
 *
 * This module is a leaf: it imports only `core` types and computes positions in
 * the focus-centred SI frame (origin at the central body's focus, periapsis on
 * the +x axis, +y up). It is the single statement of the perifocal position
 * formula; `orbit-geometry.ts` delegates its time-zero position here, so the
 * static and animated paths never drift (AD-10). No consumer re-offsets by
 * c = a*e.
 *
 * Every orbit is solved independently against the CENTRAL body's fixed reference
 * gravitational parameter `mu`; the orbiting body's own mass is negligible and its
 * mu is never read here (AC 6). Bodies exert no force on one another.
 */

/** Newton-Raphson residual target for Kepler's equation. */
const RESIDUAL_TOLERANCE = 1e-10;
/** Iteration cap: a divergence guard; convergence is reached well within this. */
const MAX_ITERATIONS = 12;
const TWO_PI = 2 * Math.PI;

/**
 * Wrap any angle in radians into the canonical range [0, 2*pi). The single
 * angle-normalisation used by the solver and by the phase inverse
 * (`meanAnomalyAtWorldPoint`), stated once here in the pure `core` leaf so
 * `state` and `render` re-use it rather than re-declaring the modulo (AD-1/AD-5).
 */
export function normalizeAngle(radians: number): number {
  return ((radians % TWO_PI) + TWO_PI) % TWO_PI;
}

/**
 * Mean motion n = sqrt(mu / a^3), radians of mean anomaly per SI second, where
 * `mu` is the central body's reference gravitational parameter.
 */
export function meanMotion(a: number, mu: number): number {
  return Math.sqrt(mu / (a * a * a));
}

/**
 * Orbital period T = 2*pi / n = 2*pi*sqrt(a^3 / mu), central-mu based, treating
 * the orbiting body's mass as negligible.
 */
export function orbitalPeriod(a: number, mu: number): number {
  return TWO_PI * Math.sqrt((a * a * a) / mu);
}

/**
 * Solve Kepler's equation M = E - e*sin(E) for the eccentric anomaly E by
 * Newton-Raphson. `meanAnomaly` is reduced modulo 2*pi first so the solver stays
 * stable over long run times. Seeds E0 = M + e*sin(M) (robust for high e near
 * periapsis) and stops when the residual |E - e*sin(E) - M| < 1e-10 or after a
 * small fixed iteration cap. Converges and stays finite for every e up to and
 * including 0.95.
 */
export function solveEccentricAnomaly(meanAnomaly: number, e: number): number {
  const M = ((meanAnomaly % TWO_PI) + TWO_PI) % TWO_PI;
  let E = M + e * Math.sin(M);
  for (let i = 0; i < MAX_ITERATIONS; i++) {
    const f = E - e * Math.sin(E) - M;
    if (Math.abs(f) < RESIDUAL_TOLERANCE) break;
    const fPrime = 1 - e * Math.cos(E);
    E -= f / fPrime;
  }
  return E;
}

/**
 * Focus-centred position of an orbiting body at a given mean anomaly `M`
 * (radians), the single perifocal position-from-mean-anomaly statement (Story
 * 1.2's motion model, reused by the drag phase model in Story 1.7). Solves
 * Kepler's equation for the eccentric anomaly E and returns the perifocal point
 *   x = a*(cos E - e),  y = a*sqrt(1 - e^2)*sin E.
 * Accepts `OrbitalElements` so both a full `Body` and a live store `{ a, e }`
 * feed identical pure math. A non-orbiting element (a = 0) has no orbit, so it
 * returns the origin. Pure and SI/radians (AD-1/AD-5).
 */
export function positionAtMeanAnomaly(
  elements: OrbitalElements,
  meanAnomaly: number
): { x: number; y: number } {
  const { a, e } = elements;
  if (a === 0) return { x: 0, y: 0 };
  const E = solveEccentricAnomaly(meanAnomaly, e);
  return {
    x: a * (Math.cos(E) - e),
    y: a * Math.sqrt(1 - e * e) * Math.sin(E)
  };
}

/**
 * Focus-centred position of an orbiting body at simulation time `timeSeconds`
 * (SI seconds since t = 0). At t = 0 mean anomaly is zero, so the body sits at
 * periapsis on the +x axis at {x: a(1 - e), y: 0}.
 *
 * `centralMu` is the CURRENT central body's gravitational parameter, supplied by
 * the caller; the solver never reads the central body itself (it stays a pure
 * leaf, AC 6). Perifocal position from the eccentric anomaly E:
 *   x = a*(cos E - e),  y = a*sqrt(1 - e^2)*sin E.
 */
export function positionAtTime(
  body: Body,
  centralMu: number,
  timeSeconds: number
): { x: number; y: number } {
  // Time-zero / degenerate short-circuit: at t = 0 the body is at periapsis
  // regardless of mu, and a non-orbiting body (a = 0, e.g. the current central
  // body) has no defined mean motion. Return periapsis without dividing by a^3.
  if (timeSeconds === 0 || body.a === 0) {
    return positionAtMeanAnomaly(body, 0);
  }
  const n = meanMotion(body.a, centralMu);
  const M = n * timeSeconds;
  return positionAtMeanAnomaly(body, M);
}
